import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './Configuracoes.css';
import api from '../services/api';
import { toast } from 'react-toastify';
import UsuariosTab from '../components/UsuariosTab';
import EmpresaCamposForm from '../components/EmpresaCamposForm';

const TABS = [
  { id: 'perfil',    label: 'Perfil',    icon: 'person'             },
  { id: 'seguranca', label: 'Segurança', icon: 'lock'               },
  { id: 'empresa',   label: 'Empresa',   icon: 'domain', adminOnly: true },
  { id: 'usuarios',  label: 'Usuários',  icon: 'group',  adminOnly: true },
  { id: 'plano',     label: 'Plano',     icon: 'workspace_premium', adminOnly: true },
];

function Configuracoes() {
  const isStaff = localStorage.getItem('is_staff') === 'true';
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get('tab');
  const [tab, setTab] = useState(tabParam || 'perfil');
  const tabs = TABS.filter((t) => !t.adminOnly || isStaff);

  return (
    <div className="cfg-page">
      <div className="cfg-page-header">
        <h2 className="cfg-page-title">Configurações</h2>
        <p className="cfg-page-sub">Gerencie seu perfil, segurança e usuários do sistema.</p>
      </div>

      <div className="cfg-layout">
        <aside className="cfg-sidebar">
          {tabs.map((t) => (
            <button
              key={t.id}
              className={`cfg-tab-btn${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <span className="material-symbols-outlined">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </aside>

        <div className="cfg-content">
          {tab === 'perfil'    && <PerfilTab />}
          {tab === 'seguranca' && <SegurancaTab />}
          {tab === 'empresa'   && isStaff && <EmpresaTab />}
          {tab === 'usuarios'  && isStaff && <UsuariosTab />}
          {tab === 'plano'     && isStaff && <PlanoTab />}
        </div>
      </div>
    </div>
  );
}

// ── Perfil ────────────────────────────────────────────────────────────────────

function PerfilTab() {
  const [form, setForm]     = useState({ first_name: '', last_name: '', email: '' });
  const [username, setUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    api.get('/perfil/', { headers: headers() })
      .then((res) => {
        setForm({
          first_name: res.data.first_name || '',
          last_name:  res.data.last_name  || '',
          email:      res.data.email      || '',
        });
        setUsername(res.data.username || '');
      })
      .catch(() => toast.error('Erro ao carregar perfil.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/perfil/', form, {
        headers: { ...headers(), 'Content-Type': 'application/json' },
      });
      localStorage.setItem('first_name', form.first_name);
      localStorage.setItem('last_name',  form.last_name);
      toast.success('Perfil atualizado com sucesso!');
    } catch {
      toast.error('Erro ao atualizar perfil.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cfg-card">
        <div className="cfg-card-accent" />
        <div className="cfg-card-body">
          <div className="skeleton" style={{ height: 160, borderRadius: 8 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="cfg-card">
      <div className="cfg-card-accent" />
      <div className="cfg-card-body">
        <header className="cfg-section-header">
          <span className="material-symbols-outlined cfg-section-icon">person</span>
          <div>
            <h3 className="cfg-section-title">Informações do Perfil</h3>
            <p className="cfg-section-sub">Atualize seus dados de identificação no sistema.</p>
          </div>
        </header>

        <form onSubmit={handleSave} className="cfg-form">
          <div className="cfg-field">
            <label className="cfg-label">Usuário</label>
            <input className="cfg-input cfg-input--readonly" value={username} readOnly />
            <span className="cfg-hint">O nome de usuário não pode ser alterado.</span>
          </div>

          <div className="cfg-row">
            <div className="cfg-field">
              <label className="cfg-label">Nome</label>
              <input
                className="cfg-input"
                type="text"
                placeholder="Seu nome"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div className="cfg-field">
              <label className="cfg-label">Sobrenome</label>
              <input
                className="cfg-input"
                type="text"
                placeholder="Seu sobrenome"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
          </div>

          <div className="cfg-field">
            <label className="cfg-label">E-mail</label>
            <input
              className="cfg-input"
              type="email"
              placeholder="email@exemplo.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>

          <div className="cfg-form-actions">
            <button type="submit" className="cfg-btn-primary" disabled={saving}>
              <span className="material-symbols-outlined">save</span>
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Segurança ─────────────────────────────────────────────────────────────────

function SegurancaTab() {
  const [form, setForm]         = useState({ senha_atual: '', nova_senha: '', confirmar_senha: '' });
  const [saving, setSaving]     = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]   = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.nova_senha !== form.confirmar_senha) {
      toast.error('A nova senha e a confirmação não conferem.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/perfil/alterar-senha/', form, {
        headers: { ...headers(), 'Content-Type': 'application/json' },
      });
      setForm({ senha_atual: '', nova_senha: '', confirmar_senha: '' });
      toast.success('Senha alterada com sucesso!');
    } catch (err) {
      const msg = err.response?.data?.senha_atual || 'Erro ao alterar senha.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfg-card">
      <div className="cfg-card-accent" />
      <div className="cfg-card-body">
        <header className="cfg-section-header">
          <span className="material-symbols-outlined cfg-section-icon">lock</span>
          <div>
            <h3 className="cfg-section-title">Segurança da Conta</h3>
            <p className="cfg-section-sub">Mantenha sua senha forte e atualizada.</p>
          </div>
        </header>

        <form onSubmit={handleSave} className="cfg-form">
          <div className="cfg-field">
            <label className="cfg-label">Senha Atual</label>
            <div className="cfg-input-wrap">
              <input
                className="cfg-input"
                type={showCurrent ? 'text' : 'password'}
                placeholder="••••••••"
                value={form.senha_atual}
                onChange={(e) => setForm({ ...form, senha_atual: e.target.value })}
                required
              />
              <button
                type="button"
                className="cfg-pw-toggle"
                onClick={() => setShowCurrent((v) => !v)}
                aria-label="Mostrar/ocultar senha atual"
              >
                <span className="material-symbols-outlined">
                  {showCurrent ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
          </div>

          <div className="cfg-row">
            <div className="cfg-field">
              <label className="cfg-label">Nova Senha</label>
              <div className="cfg-input-wrap">
                <input
                  className="cfg-input"
                  type={showNew ? 'text' : 'password'}
                  placeholder="Mínimo 8 caracteres"
                  minLength={8}
                  value={form.nova_senha}
                  onChange={(e) => setForm({ ...form, nova_senha: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="cfg-pw-toggle"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label="Mostrar/ocultar nova senha"
                >
                  <span className="material-symbols-outlined">
                    {showNew ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>
            <div className="cfg-field">
              <label className="cfg-label">Confirmar Nova Senha</label>
              <div className="cfg-input-wrap">
                <input
                  className="cfg-input"
                  type={showNew ? 'text' : 'password'}
                  placeholder="Repita a nova senha"
                  value={form.confirmar_senha}
                  onChange={(e) => setForm({ ...form, confirmar_senha: e.target.value })}
                  required
                />
              </div>
            </div>
          </div>

          <div className="cfg-form-actions">
            <button type="submit" className="cfg-btn-primary" disabled={saving}>
              <span className="material-symbols-outlined">lock_reset</span>
              {saving ? 'Alterando...' : 'Alterar Senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Empresa (dono/admin edita a própria empresa) ──────────────────────────────

const EMPTY_EMPRESA_FORM = {
  nome: '', nome_fantasia: '', razao_social: '', cnpj: '', ie: '', im: '', telefones: [], ativo: true,
};

function EmpresaTab() {
  const fileInputRef = useRef(null);
  const [form, setForm]         = useState(EMPTY_EMPRESA_FORM);
  const [logo, setLogo]         = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    api.get('/empresas/minha/', { headers: headers() })
      .then((res) => {
        const empresa = res.data;
        setForm({
          nome:          empresa.nome,
          nome_fantasia: empresa.nome_fantasia || '',
          razao_social:  empresa.razao_social  || '',
          cnpj:          empresa.cnpj          || '',
          ie:            empresa.ie            || '',
          im:            empresa.im            || '',
          telefones:     empresa.telefones     || [],
          ativo:         empresa.ativo,
        });
        setLogoPreview(empresa.logo || null);
      })
      .catch(() => toast.error('Erro ao carregar dados da empresa.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'telefones') formData.append(key, JSON.stringify(value));
        else if (key === 'ativo') return; // somente leitura pro dono/admin
        else formData.append(key, value);
      });
      if (logo) formData.append('logo', logo);

      const res = await api.patch('/empresas/minha/', formData, { headers: headers() });
      setLogo(null);
      setLogoPreview(res.data.logo || null);
      toast.success('Dados da empresa atualizados.');
    } catch (err) {
      const data = err.response?.data;
      const msg = typeof data === 'object'
        ? Object.values(data).flat().join(' ')
        : 'Erro ao salvar dados da empresa.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cfg-card">
        <div className="cfg-card-accent" />
        <div className="cfg-card-body">
          <div className="skeleton" style={{ height: 160, borderRadius: 8 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="cfg-card">
      <div className="cfg-card-accent" />
      <div className="cfg-card-body">
        <header className="cfg-section-header">
          <span className="material-symbols-outlined cfg-section-icon">domain</span>
          <div>
            <h3 className="cfg-section-title">Dados da Empresa</h3>
            <p className="cfg-section-sub">Informações fiscais e de marca usadas em orçamentos e documentos.</p>
          </div>
        </header>

        <form onSubmit={handleSave} className="cfg-form">
          <EmpresaCamposForm
            form={form}
            setForm={setForm}
            logoPreview={logoPreview}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            mostrarAtivo={false}
          />

          <div className="cfg-form-actions">
            <button type="submit" className="cfg-btn-primary" disabled={saving}>
              <span className="material-symbols-outlined">save</span>
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Plano ─────────────────────────────────────────────────────────────────────

const PLANO_INFO = {
  trial:      { label: 'Trial',      color: '#6b7280', badge: '#f3f4f6', text: '#374151' },
  starter:    { label: 'Starter',    color: '#2563eb', badge: '#eff6ff', text: '#1d4ed8' },
  pro:        { label: 'Pro',        color: '#D4AF37', badge: '#fffbeb', text: '#92400e' },
  business:   { label: 'Business',   color: '#7c3aed', badge: '#f5f3ff', text: '#5b21b6' },
  enterprise: { label: 'Enterprise', color: '#0D1B2A', badge: '#f3f4f6', text: '#0D1B2A' },
};

const RECURSO_LABELS = {
  produtos:       'Produtos',
  clientes:       'Clientes',
  orcamentos_mes: 'Orçamentos / mês',
  usuarios:       'Usuários',
};

function UsageMeter({ label, used, limit }) {
  const pct = limit == null ? 0 : Math.min((used / limit) * 100, 100);
  const isWarning = limit != null && used / limit >= 0.8;
  const isFull    = limit != null && used >= limit;
  const barColor  = isFull ? '#dc2626' : isWarning ? '#d97706' : '#2563eb';

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</span>
        <span style={{ fontSize: 13, color: isFull ? '#dc2626' : '#6b7280', fontWeight: isFull ? 700 : 400 }}>
          {limit == null ? `${used} / ilimitado` : `${used} / ${limit}`}
        </span>
      </div>
      <div style={{ height: 8, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
        {limit != null && (
          <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 999, transition: 'width 0.4s' }} />
        )}
      </div>
    </div>
  );
}

const PLANOS_TABLE = [
  { recurso: 'Produtos',           trial: '20',    starter: '200',    pro: '2.000',      business: 'Ilimitado' },
  { recurso: 'Clientes',           trial: '20',    starter: '200',    pro: '2.000',      business: 'Ilimitado' },
  { recurso: 'Orçamentos / mês',   trial: '10',    starter: '100',    pro: 'Ilimitado',  business: 'Ilimitado' },
  { recurso: 'Usuários',           trial: '1',     starter: '5',      pro: '20',         business: 'Ilimitado' },
  { recurso: 'Suporte',            trial: 'Email', starter: 'Email',  pro: 'Prioritário', business: 'Prioritário' },
  { recurso: 'Relatórios',         trial: '—',     starter: 'Básico', pro: 'Avançado',   business: 'Avançado' },
];

const PLANOS_ORDEM = ['trial', 'starter', 'pro', 'business'];

function PlanoTab() {
  const [dados, setDados]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/plano/usos/')
      .then((res) => setDados(res.data))
      .catch(() => toast.error('Erro ao carregar dados do plano.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="cfg-card">
        <div className="cfg-card-accent" />
        <div className="cfg-card-body">
          <div className="skeleton" style={{ height: 200, borderRadius: 8 }} />
        </div>
      </div>
    );
  }

  if (!dados) return null;

  const plano = dados.plano || 'trial';
  const info  = PLANO_INFO[plano] || PLANO_INFO.trial;
  const usos  = dados.usos || {};
  const lims  = dados.limites || {};

  const whatsappMsg = encodeURIComponent(
    `Olá! Gostaria de fazer upgrade do meu plano no Hope System. Plano atual: ${info.label}.`
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Current plan card */}
      <div className="cfg-card">
        <div className="cfg-card-accent" />
        <div className="cfg-card-body">
          <header className="cfg-section-header" style={{ marginBottom: 20 }}>
            <span className="material-symbols-outlined cfg-section-icon">workspace_premium</span>
            <div>
              <h3 className="cfg-section-title">Plano Atual</h3>
              <p className="cfg-section-sub">Uso dos recursos da sua empresa este mês.</p>
            </div>
            <span style={{
              marginLeft: 'auto', padding: '4px 14px', borderRadius: 999,
              background: info.badge, color: info.text,
              fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
            }}>
              {info.label}
            </span>
          </header>

          {Object.entries(RECURSO_LABELS).map(([key, label]) => (
            <UsageMeter
              key={key}
              label={label}
              used={usos[key] ?? 0}
              limit={lims[key] ?? null}
            />
          ))}

          {plano !== 'business' && plano !== 'enterprise' && (
            <div style={{ marginTop: 20, padding: '14px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="material-symbols-outlined" style={{ color: '#d97706', fontSize: 20 }}>info</span>
              <p style={{ margin: 0, fontSize: 13, color: '#92400e', flex: 1 }}>
                Faça upgrade para aumentar seus limites e desbloquear recursos avançados.
              </p>
              <a
                href={`https://wa.me/5571996297105?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '7px 16px', borderRadius: 8, background: '#0D1B2A', color: '#D4AF37',
                  fontFamily: 'Montserrat, sans-serif', fontSize: 12, fontWeight: 700,
                  textDecoration: 'none', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>chat</span>
                Falar com suporte
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Plans comparison table */}
      <div className="cfg-card">
        <div className="cfg-card-accent" />
        <div className="cfg-card-body">
          <header className="cfg-section-header" style={{ marginBottom: 20 }}>
            <span className="material-symbols-outlined cfg-section-icon">compare</span>
            <div>
              <h3 className="cfg-section-title">Comparativo de Planos</h3>
              <p className="cfg-section-sub">Escolha o plano ideal para o seu negócio.</p>
            </div>
          </header>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>Recurso</th>
                  {PLANOS_ORDEM.map((p) => (
                    <th key={p} style={{
                      textAlign: 'center', padding: '10px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      background: p === plano ? '#f0f9ff' : 'transparent',
                      color: PLANO_INFO[p].color, fontWeight: 700,
                      borderRadius: p === plano ? '8px 8px 0 0' : 0,
                    }}>
                      {PLANO_INFO[p].label}
                      {p === plano && (
                        <span style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#6b7280', marginTop: 2 }}>Atual</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PLANOS_TABLE.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding: '10px 12px', color: '#374151', fontWeight: 500 }}>{row.recurso}</td>
                    {PLANOS_ORDEM.map((p) => (
                      <td key={p} style={{
                        textAlign: 'center', padding: '10px 16px',
                        color: row[p] === '—' ? '#d1d5db' : '#374151',
                        background: p === plano ? '#f0f9ff' : 'transparent',
                        fontWeight: p === 'business' ? 600 : 400,
                      }}>
                        {row[p]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {plano !== 'business' && plano !== 'enterprise' && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
              {plano === 'trial' && (
                <a
                  href={`https://wa.me/5571996297105?text=${encodeURIComponent('Olá! Quero contratar o plano Starter do Hope System.')}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    padding: '10px 24px', borderRadius: 8, background: '#2563eb', color: '#fff',
                    fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 700,
                    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>rocket_launch</span>
                  Assinar Starter
                </a>
              )}
              {plano !== 'pro' && (
                <a
                  href={`https://wa.me/5571996297105?text=${encodeURIComponent('Olá! Quero contratar o plano Pro do Hope System.')}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    padding: '10px 24px', borderRadius: 8, background: '#D4AF37', color: '#0D1B2A',
                    fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 700,
                    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>workspace_premium</span>
                  Assinar Pro
                </a>
              )}
              <a
                href={`https://wa.me/5571996297105?text=${encodeURIComponent('Olá! Quero contratar o plano Business do Hope System.')}`}
                target="_blank" rel="noopener noreferrer"
                style={{
                  padding: '10px 24px', borderRadius: 8, background: '#7c3aed', color: '#fff',
                  fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 700,
                  textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>diamond</span>
                Assinar Business
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Configuracoes;
